/* What a given client can actually do in their portal right now — respecting the
   studio's on/off toggles (`portalHidden`), the stage curation (setup rooms fall
   away once the project is in delivery) and each feature's own gate. Used to make
   the invitation introduce only the features the client truly has access to. */

import type { Project } from "./types";

const SETUP = new Set(["brief", "moodboard", "direction", "configure", "engagement"]);

/** True once the project is at documentation or beyond — setup rooms are curated
    out (mirrors the portal's own `portalExecuting`). */
function executing(p: Project): boolean {
  const stages = (p as any).stages ?? [];
  const activeIdx = stages.findIndex((s: any) => s.status === "active");
  const curIdx = activeIdx >= 0 ? activeIdx : stages.length - 1;
  const docIdx = stages.findIndex((s: any) => s.key === "documentation");
  return docIdx >= 0 && curIdx >= docIdx;
}

interface FeatureDef { key: string; blurb: string; setup?: boolean; gate?: (p: Project) => boolean }
/* Ordered so the most engaging capabilities lead the introduction. */
const CATALOG: FeatureDef[] = [
  { key: "brief", blurb: "shape the design brief", setup: true },
  { key: "moodboard", blurb: "share inspiration on the mood board", setup: true },
  { key: "direction", blurb: "choose a design direction", setup: true, gate: (p) => !!(p as any).conceptsPresentedOn && ((p as any).concepts?.length ?? 0) > 0 },
  { key: "concept", blurb: "read the design concept and choose between the options", gate: (p) => (p as any).deck?.status === "issued" || ((p as any).deckArchive ?? []).some((d: any) => d.status === "issued") },
  { key: "film", blurb: "watch the film about the design", gate: (p) => ((p as any).films ?? []).some((f: any) => f.sharedWithClient) },
  { key: "designs", blurb: "review and approve the designs" },
  { key: "drawings", blurb: "view the construction drawings in full quality" },
  { key: "budget", blurb: "size the project against your budget" },
  { key: "story", blurb: "follow the project story" },
  { key: "spaces", blurb: "walk through your spaces room by room", gate: (p) => (((p as any).brief?.spaces?.length ?? 0) > 0) },
  { key: "approvals", blurb: "formally sign off the drawings", gate: (p) => (((p as any).approvals?.length ?? 0) > 0) },
  { key: "reviews", blurb: "mark up drawings and request changes" },
  { key: "rfis", blurb: "raise and track RFIs", gate: (p) => (((p as any).rfis?.length ?? 0) > 0) || executing(p) },
  { key: "packages", blurb: "download the drawing packages shared with you", gate: (p) => ((p as any).packages ?? []).some((s: any) => s.audience === "client") },
  { key: "submissions", blurb: "view, download and share each completed design stage in full", gate: (p) => (((p as any).submissions ?? []).length > 0) },
  { key: "tender", blurb: "invite contractors to tender and see who registers" },
  { key: "progress", blurb: "follow progress and key dates" },
  { key: "consultants", blurb: "appoint the structural & MEP consultants" },
  { key: "reports", blurb: "read the progress reports" },
  { key: "fees", blurb: "see the fee and exactly what it covers" },
  { key: "configure", blurb: "explore the scope", setup: true, gate: (p) => !(p as any).billing },
  { key: "engagement", blurb: "review and sign the engagement", setup: true },
  { key: "billing", blurb: "see invoices and pay online" },
  { key: "messages", blurb: "message the design team directly" },
];

/** The short capability phrases the client currently has access to. */
export function clientFeatureLines(p: Project): string[] {
  const hidden = new Set((p as any).portalHidden ?? []);
  const exec = executing(p);
  return CATALOG
    .filter((f) => !hidden.has(f.key) && !(exec && f.setup) && (!f.gate || f.gate(p)))
    .map((f) => f.blurb);
}

/** Join the phrases into a natural sentence fragment, capped so the invite stays
    short: "do A, B, C and more". */
export function clientFeatureSentence(p: Project, max = 5): string {
  const lines = clientFeatureLines(p);
  if (!lines.length) return "follow your project in one place";
  const shown = lines.slice(0, max);
  const more = lines.length > max;
  if (shown.length === 1) return shown[0] + (more ? " and more" : "");
  const head = shown.slice(0, -1).join(", ");
  const tail = shown[shown.length - 1];
  return `${head} and ${tail}${more ? ", and more" : ""}`;
}
